import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  formatHuman,
  formatYmd,
  monthGrid,
  monthOptions,
  parseYmd,
  yearRange,
} from '../lib/calendar.ts';

/**
 * Controlled date field with a custom calendar popover. Unlike a native
 * `<input type="date">`, the month ‹ › arrows only mutate internal view state —
 * navigating months NEVER closes the popover (the reported native-picker
 * annoyance). Picking a day commits via onChange and closes; outside-click /
 * Escape close without changing the value.
 *
 * The popover is portaled to document.body with fixed positioning so it floats
 * above table cards and modals instead of being clipped. Inside a modal, the
 * portal still sits in the React tree under the modal's panel, so its clicks
 * bubble to the panel's stopPropagation (the modal won't close); the Escape
 * handler uses the capture phase so it dismisses only the calendar, not the
 * surrounding modal.
 *
 * Value format is `YYYY-MM-DD` (or '' for none) — the same the API date helpers
 * (toDateInput/fromDateInput) already use.
 */
const PANEL_WIDTH = 272;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function DateField({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Date',
  placeholder = 'Select date',
  className = '',
  invalid = false,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [view, setView] = useState(() => initialView(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const parsed = parseYmd(value);

  function reposition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8));
    setPos({ top: r.bottom + 4, left });
  }

  function openPanel() {
    setView(initialView(value)); // re-seat on the selected month (or today)
    reposition();
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    // Capture phase so an open calendar swallows Escape before a surrounding
    // modal's document-level handler can also act on it.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onReposition = () => reposition();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const weeks = useMemo(() => monthGrid(view.year, view.month), [view]);

  function selectDay(day: number) {
    onChange(formatYmd({ year: view.year, month: view.month, day }));
    setOpen(false);
  }

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={ariaLabel}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_WIDTH }}
            className="z-50 rounded-md border border-slate-200 bg-white p-2 text-sm shadow-lg"
          >
            <div className="mb-1 flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setView((v) => addMonths(v.year, v.month, -1))}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                ‹
              </button>
              <div className="flex flex-1 items-center justify-center gap-1">
                <select
                  aria-label="Month"
                  disabled={disabled}
                  value={view.month}
                  onChange={(e) => setView((v) => ({ ...v, month: Number(e.target.value) }))}
                  className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs text-slate-700 focus:border-slate-400 focus:outline-none disabled:opacity-50"
                >
                  {monthOptions().map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Year"
                  disabled={disabled}
                  value={view.year}
                  onChange={(e) => setView((v) => ({ ...v, year: Number(e.target.value) }))}
                  className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs text-slate-700 focus:border-slate-400 focus:outline-none disabled:opacity-50"
                >
                  {yearRange(new Date().getFullYear(), parsed?.year ?? null).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setView((v) => addMonths(v.year, v.month, 1))}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                ›
              </button>
            </div>

            <div className="grid grid-cols-7 text-center text-[10px] font-medium uppercase text-slate-400">
              {WEEKDAYS.map((w) => (
                <span key={w} className="py-1">
                  {w}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {weeks.flat().map((day, i) => {
                if (day === null) return <span key={i} />;
                const selected =
                  parsed?.year === view.year &&
                  parsed?.month === view.month &&
                  parsed?.day === day;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => selectDay(day)}
                    aria-pressed={selected}
                    className={`rounded py-1 text-xs ${
                      selected
                        ? 'bg-[#4D148C] font-medium text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            <div className="mt-1 flex items-center justify-between border-t border-slate-100 pt-1">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  const n = new Date();
                  onChange(formatYmd({ year: n.getFullYear(), month: n.getMonth(), day: n.getDate() }));
                  setOpen(false);
                }}
                className="rounded px-1.5 py-0.5 text-xs font-medium text-[#4D148C] hover:bg-[#F3EBFA]"
              >
                Today
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={`flex items-center gap-2 rounded border px-2 py-1 text-xs text-slate-700 focus:outline-none disabled:opacity-50 ${
          invalid
            ? 'border-red-400 ring-2 ring-red-100 hover:border-red-500 focus:border-red-500'
            : 'border-slate-300 hover:border-slate-400 focus:border-slate-400'
        }`}
      >
        <span className={parsed ? '' : 'text-slate-400'}>
          {parsed ? formatHuman(parsed) : placeholder}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 text-slate-400"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </button>
      {panel}
    </div>
  );
}

// Which month the calendar shows on open: the selected date's month, else the
// current month.
function initialView(value: string): { year: number; month: number } {
  const p = parseYmd(value);
  if (p) return { year: p.year, month: p.month };
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}
